import { z } from 'zod';
import { conversationSettingsSchema, pageAgentPlaceSchema } from '../request-schemas';

/**
 * `@persona` in a conversation (`POST /api/conversations/:id/persona-reply`): one of the account's
 * saved agents answers once in the thread, with its recent transcript as context; the
 * conversation stays with its own agent.
 */
export const personaReplyRequestSchema = z
  .object({
    conversationId: z.uuid(),
    profileId: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/),
    message: z
      .object({
        id: z.string().min(1).max(256),
        text: z.string().trim().min(1).max(100_000),
      })
      .strict(),
    place: pageAgentPlaceSchema,
    settings: conversationSettingsSchema,
  })
  .strict();
export type PersonaReplyRequest = z.infer<typeof personaReplyRequestSchema>;

/** The key of a persona's reply in its assistant message's metadata. */
export const PERSONA_METADATA_KEY = 'persona';

/** Who answered a message: the saved agent, its name, its runtime's name, its avatar. */
export const personaReplyMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  agentName: z.string().nullable(),
  avatar: z.string().optional(),
  /**
   * The reply was generated aside, read-only and without tools: the persona runs on another
   * runtime than the conversation's agent, so it cannot take a turn of this conversation.
   */
  readOnly: z.boolean().optional(),
});
export type PersonaReplyMetadata = z.infer<typeof personaReplyMetadataSchema>;

/** The user's message, then the persona's reply (`CrowdexUIMessage`s), as stored. */
export const personaReplyResponseSchema = z.object({ messages: z.array(z.unknown()) });
export type PersonaReplyResponse = z.infer<typeof personaReplyResponseSchema>;

/** The persona that wrote a message; null for the conversation's own agent. */
export function personaOfMessage(message: { metadata?: unknown }): PersonaReplyMetadata | null {
  const metadata = message.metadata;
  if (typeof metadata !== 'object' || metadata === null) return null;
  const parsed = personaReplyMetadataSchema.safeParse(
    (metadata as Record<string, unknown>)[PERSONA_METADATA_KEY],
  );
  return parsed.success ? parsed.data : null;
}
